import Avatar from '@mui/material/Avatar';
import React, { ReactNode } from 'react';
import {
	Box,
	Button,
	Divider,
	FormControl,
	IconButton,
	Menu,
	MenuItem,
	Select,
	Toolbar,
	Tooltip,
	Typography,
	useMediaQuery,
} from '@mui/material';
import { getDecodedToken, deleteCookie, FetchFinOpsOverviewCloudCost, fetchDataEstateLastSyncData } from '../../service/service';
import { usePepwiseAuthController, setAuthenticated, setAuthToken } from 'Context/contexts/AuthContext';
import { ReactComponent as HomeIcon } from '../../assets/PepWise2.0/Home.svg';
import { ReactComponent as UserIcon } from '../../assets/PepWise2.0/User.svg';
import { ReactComponent as LogoutIcon } from '../../assets/PepWise2.0/logout.svg';
import moment from 'moment';
import { useTheme } from "@mui/material/styles";
import { Link, useLocation, useNavigate } from 'react-router-dom';
import TabPageHeader from './TabPageHeader';
import NotificationBar from 'components/landingScreen/notificationBar';
import { setTeradataStorageLastSyncDate, setAzureCostSummaryLastSyncDate, setDemetricsLastSyncDate } from 'Context/contexts/cacheData/DataEstate';
import { useCacheDataController } from 'Context/contexts/CacheDataContext';
import GlobalSearch from 'components/GlobalSearch/GlobalSearch';
import { ReactComponent as HelpIcon } from '../../assets/PepWise2.0/Help_App Tour Icon.svg';
import InfoIcon from '@mui/icons-material/Info';
import DynamicBannerOverlay from 'components/DynamicBanner/DynamicBannerOverlay';
import { getBannerViewerFromToken } from 'components/DynamicBanner/bannerUtils';

const ssoLoginLocalStore = localStorage.getItem('sso-login');
const ssoLogin = ssoLoginLocalStore ? ssoLoginLocalStore === 'true' : process.env.REACT_APP_SSO_LOGIN === 'true';
const sessionCookie = 'id_token';

interface Props {
	memberData: any;
	notificationData?: any;
	showLandingPageNotification?: () => ReactNode;
	showFullScreen?: () => ReactNode;
}

const Header: React.FC<Props> = ({ memberData, notificationData, showLandingPageNotification, showFullScreen }) => {

	const theme = useTheme();
	const colors = theme.palette;
	const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const [controller, dispatch] = usePepwiseAuthController();
	const { capabilities } = controller;

	const location = useLocation();
	const navigate = useNavigate();
	const bannerViewer = getBannerViewerFromToken(getDecodedToken(), capabilities);
	const isLandingPage = location.pathname === '/LandingPage';
	const isDataEstateRoute = location.pathname?.includes('/dataestate');

	const searchParams = new URLSearchParams(location.search);
	const hasSectorParam = location.pathname.includes('/data-pipeline/executiveTemplate') && searchParams.has('sector');

	const fromDashboardType = searchParams.get('fromType') as 'sector' | 'program' | null;
	const fromProgramName = searchParams.get('fromProgram');

	const handleHelpClick = () => {
		window.dispatchEvent(new Event("start-joyride"));
	};

	// Each key is a route path; value has a label and optional parent path
	type Node = { label: string; parent?: string };

	const breadcrumbIndex: Record<string, Node> = {
		'/dataestate': { label: 'D&AI Executive Dashboard' },
		'/DAIXOps': { label: 'D&AI Monthly XOps Dashboards' },
		'/DAIXOps/summary/overview': { label: 'Summary', parent: '/DAIXOps' },

		// Data pipeline paths
		'/dashboard/dataOpsLanding': { label: 'Data Pipeline Observability' },

		'/data-pipeline/dataOps': { label: 'DataOps Sustain', parent: '/dashboard/dataOpsLanding' },
		'/data-pipeline/programs': { label: '', parent: '/data-pipeline/dataOps' },
		'/data-pipeline/projects': { label: '', parent: '/data-pipeline/programs' },
		'/data-pipeline/programs-tab': { label: 'DataOps Sustain', parent: '/dashboard/dataOpsLanding' },
		'/data-pipeline/executiveTemplate': { label: hasSectorParam ? 'End to End Observability Dashboard' : 'DataOps Sustain', parent: '/dashboard/dataOpsLanding' },

		'/data-pipeline/programOpsDashboard': { label: 'Program Ops Dashboards', parent: '/dashboard/dataOpsLanding' },
		'/data-pipeline/programOps/IBP/list': { label: 'IBP', parent: '/data-pipeline/programOpsDashboard' },
		'/data-pipeline/programOps/IBP/IBP%20-%20Global/list': { label: 'IBP', parent: '/data-pipeline/programOpsDashboard' },
		'/data-pipeline/programOps/IBP/IBP%20Global%20-%20ReactJS%20POC/list': { label: 'IBP', parent: '/data-pipeline/programOpsDashboard' },
		'/data-pipeline/programOps/IBP/markets': { label: 'IBP', parent: '/data-pipeline/programOpsDashboard' },
		'/data-pipeline/programOps/IBP/markets/ibpDashboard': { label: 'IBP Global ReactJS', parent: '/data-pipeline/programOps/IBP/markets' },
		'/data-pipeline/ibpDashboard/gdo-details': { label: 'IBP Global ReactJS', parent: '/data-pipeline/programOps/IBP/markets' },
		'/data-pipeline/ibpDashboard/stage-details': { label: 'IBP Global ReactJS', parent: '/data-pipeline/programOps/IBP/markets' },
		'/data-pipeline/ibpDashboard/gdoTabs': { label: 'IBP Global ReactJS', parent: '/data-pipeline/programOps/IBP/markets' },
		'/data-pipeline/programOps/MDIP/list': { label: 'MDIP', parent: '/data-pipeline/programOpsDashboard' },
		'/data-pipeline/programOps/EDF/list': { label: 'EDF', parent: '/data-pipeline/programOpsDashboard' },
		'/data-pipeline/programOps/Mosaic/list': { label: 'Mosaic', parent: '/data-pipeline/programOpsDashboard' },

		'/data-pipeline/home/executiveOps': { label: 'End to End Observability Dashboard', parent: '/dashboard/dataOpsLanding' },
		'/data-pipeline/executiveOps/sector': { label: 'Sector Dashboards', parent: '/data-pipeline/home/executiveOps' },
		'/data-pipeline/executiveOps/program': {label: 'Program Dashboards', parent: '/data-pipeline/home/executiveOps'},
		'/data-pipeline/executiveOps/pepsellPepIrisRecon': {label: 'PEPSELL PEPIRIS RECON DASHBOARD', parent: '/data-pipeline/home/executiveOps'},

		'/dataOps/platformHealth/summary': { label: 'Platform Health', parent: '/dashboard/dataOpsLanding' },
		'/dataOps/integratedJobMonitoring': { label: 'Integrated Job Monitoring Dashboard', parent: '/dashboard/dataOpsLanding' },
		'/dataOps/integratedJobMonitoring/dataAssets': { label: 'Data Assets', parent: '/dataOps/integratedJobMonitoring' },
		'/dataOps/integratedJobMonitoring/metadataUpdates': { label: 'Metadata Updates', parent: '/dataOps/integratedJobMonitoring' },
		'/dataOps/crossFlowNavigator': { label: 'CrossFlow Navigator', parent: '/dashboard/dataOpsLanding' },


		//   Program Executive Dashboard

		'/dashboard/programExecutiveLanding': { label: 'Program Executive Dashboard' },

		'/ibp/pipelines': { label: 'Integrated Business Planning (IBP)', parent: '/dashboard/programExecutiveLanding' },
		'/ibp/dashboard': { label: 'Integrated Business Planning (IBP)', parent: '/dashboard/programExecutiveLanding' },
		'/ibp/executive': { label: 'Integrated Business Planning (IBP)', parent: '/dashboard/programExecutiveLanding' },
		'/ibp/bdr-pbi': { label: 'Integrated Business Planning (IBP)', parent: '/dashboard/programExecutiveLanding' },

		'/mdip/dashboard': { label: 'MDIP Executive Dashboard', parent: '/dashboard/programExecutiveLanding' },
		'/mdip/job': { label: 'MDIP Executive Dashboard', parent: '/dashboard/programExecutiveLanding' },

		//   Finance(FinOps) Dashboard
		'/dashboard/financeOpsLanding': { label: 'Finance(FinOps) Dashboard' },

		'/finops': { label: 'Resource Mapping', parent: '/dashboard/financeOpsLanding' },
		'/platformCostObs/dashboard': { label: 'Platform Cost', parent: '/dashboard/financeOpsLanding' },

		//   Platform Observability Dashboard
		'/dashboard/platformCostLanding': { label: 'Platform Observability' },

		// Teradata Jobs
		'/dashboard/business': { label: 'Teradata Jobs', parent: '/dashboard/platformCostLanding' },
		'/jobs': { label: 'Teradata Jobs', parent: '/dashboard/platformCostLanding' },
		'/jobExecutions': { label: 'Teradata Jobs', parent: '/dashboard/platformCostLanding' },
		'/jobSchedules': { label: 'Teradata Jobs', parent: '/dashboard/platformCostLanding' },
		'/job/Infrastructure': { label: 'Teradata Jobs', parent: '/dashboard/platformCostLanding' },
		'/dashboard/administrative': { label: 'Teradata Jobs', parent: '/dashboard/platformCostLanding' },
		'/dashboard/operational': { label: 'Teradata Jobs', parent: '/dashboard/platformCostLanding' },
		'/dashboard/jobPerformance': { label: 'Teradata Jobs', parent: '/dashboard/platformCostLanding' },
		'/master/reasonsOfSLA': { label: 'Teradata Jobs', parent: '/dashboard/platformCostLanding' },

		// Tableau
		'/tableau/summary/dashboard': { label: 'Tableau', parent: '/dashboard/platformCostLanding' },
		'/tableau/performance/dashboard': { label: 'Tableau', parent: '/dashboard/platformCostLanding' },
		'/tableau/governance/dashboard': { label: 'Tableau', parent: '/dashboard/platformCostLanding' },
		'/tableau/infrastructure/dashboard': { label: 'Tableau', parent: '/dashboard/platformCostLanding' },
		'/tableau': { label: 'Tableau', parent: '/dashboard/platformCostLanding' },
		'/tableau/admin/dashboard': { label: 'Tableau', parent: '/dashboard/platformCostLanding' },

		// Data Growth
		'/dataGrowth/azureStorage': { label: 'Data Growth', parent: '/dashboard/platformCostLanding' },
		'/dataGrowth/dashboard': { label: 'Data Growth', parent: '/dashboard/platformCostLanding' },
		'/dataGrowthDataGrowth/dashboard': { label: 'Data Growth', parent: '/dashboard/platformCostLanding' },
		'/dataGrowthDatabricks/dashboard': { label: 'Data Growth', parent: '/dashboard/platformCostLanding' },
		'/dataGrowthPresto/dashboard': { label: 'Data Growth', parent: '/dashboard/platformCostLanding' },
		'/dataGrowthTableau/dashboard': { label: 'Data Growth', parent: '/dashboard/platformCostLanding' },
		'/dataGrowth': { label: 'Data Growth', parent: '/dashboard/platformCostLanding' },

		//Databricks
		'/databricksADB/workspace': { label: 'Databricks', parent: '/dashboard/platformCostLanding' },
		'/databricksADB/jobsADB': { label: 'Databricks', parent: '/dashboard/platformCostLanding' },
		'/databricksADB/clusteranalytics': { label: 'Databricks', parent: '/dashboard/platformCostLanding' },
		'/databricksADB/dbsqlperformance': { label: 'Databricks', parent: '/dashboard/platformCostLanding' },
		'/databricksADB/costmanagement': { label: 'Databricks', parent: '/dashboard/platformCostLanding' },
		'/databricksADB': { label: 'Databricks', parent: '/dashboard/platformCostLanding' },

		//Presto
		'/presto/dashboard': { label: 'Presto', parent: '/dashboard/platformCostLanding' },
		'/presto': { label: 'Presto', parent: '/dashboard/platformCostLanding' },

		//Power BI
		// '/powerBI/capacity/dashboard': { label: 'Power BI', parent: '/dashboard/platformCostLanding' },
		// '/powerBI/dataset/dashboard': { label: 'Power BI', parent: '/dashboard/platformCostLanding' },
		// '/powerBI/usage/dashboard': { label: 'Power BI', parent: '/dashboard/platformCostLanding' },
		// '/powerBI/infrastructure/dashboard': { label: 'Power BI', parent: '/dashboard/platformCostLanding' },
		// '/powerBI/capacityObservability/dashboard': { label: 'Power BI', parent: '/dashboard/platformCostLanding' },
		// '/powerBI/adoptionMetrics/dashboard': { label: 'Power BI', parent: '/dashboard/platformCostLanding' },
		'/powerBI/userActivity/dashboard': { label: 'Power BI', parent: '/dashboard/platformCostLanding' },
		'/powerBI': { label: 'Power BI', parent: '/dashboard/platformCostLanding' },

		//Data Management Framework
		'/dataManagementFramework/healthSummary/dashboard': {
			label: 'Data Management Framework',
			parent: '/dashboard/platformCostLanding'
		},
		'/dataManagementFramework/Catalogues/dashboard': {
			label: 'Data Management Framework',
			parent: '/dashboard/platformCostLanding'
		},
		'/dataManagementFramework': { label: 'Data Management Framework', parent: '/dashboard/platformCostLanding' },

		//Enterprise Platform Modernization Dashboard
		'/enterprisePlatform/dashboard': {
			label: 'Enterprise Platform Modernization Dashboard',
			parent: '/dashboard/platformCostLanding'
		},
		'/enterprisePlatform': {
			label: 'Enterprise Platform Modernization Dashboard',
			parent: '/dashboard/platformCostLanding'
		},

		//  ML Observability
		'/machineLearning': { label: 'ML Observability' },

		//   Data KPI and Alerts
		'/dashboard/alertKpiLanding': { label: 'Data KPI and Alerts' },

		'/kpiFramework/configs': { label: 'KPI Store', parent: '/dashboard/alertKpiLanding' },
		'/alertFramework/alert': { label: 'Alert Framework', parent: '/dashboard/alertKpiLanding' },
		'/dataops': { label: 'Pipeline Early Warning System', parent: '/dashboard/dataOpsLanding' },

		'/productCatalog': { label: 'Product Catalog' },
		'/productSuccessDashboard': { label: 'Product Success' },

		// Credential Management
		'/credsManagement/summary': { label: 'Credential Management Summary' },

		// XOPs pages
		'/dashboard/xops-intelligence': { label: 'XOps Intelligence' },
		'/dashboard/D&AIXOps': { label: 'D&AI Monthly XOps Dashboards' },
		'/D&AIXOps/Summary': { label: 'XOps Overview', parent: '/dashboard/D&AIXOps' },
		'/D&AIXOps/experience': { label: 'Experience Management', parent: '/dashboard/D&AIXOps' },
		'/D&AIXOps/itsm': { label: 'ITSM Operations Dashboards - By ITSM Type', parent: '/dashboard/D&AIXOps' },
		'/D&AIXOps/segments': { label: 'ITSM Operations Dashboards - By Segments', parent: '/dashboard/D&AIXOps' },
		'/D&AIXOps/service': { label: 'Service Operations', parent: '/dashboard/D&AIXOps' },
		'/finops/executive': { label: 'FINOPS EXECUTIVE DASHBOARD' },
		'/finops/executive-dashboard': { label: 'FINOPS EXECUTIVE DASHBOARD'},
		'/finopsExecutive/cloud-spend-report': { label: 'D&AI FINOPS REPORT', parent: '/finops/executive'  },
		'/finopsExecutive/pre-aop-2027': { label: 'PRE-AOP 2027', parent: '/finops/executive' },
		'/finopsExecutive/edf-cost-overview': { label: 'EDF COST OVERVIEW', parent: '/finops/executive'  },
		'/finopsExecutive/2026-dissection': { label: '2026 Dissection', parent: '/finops/executive' },
		'/D&AIXOps/finopsMetrics/insightView': { label: 'FINOPS EXECUTIVE DASHBOARD' },
		'/D&AIXOps/comp-inv/governance': { label: 'D&AI Component Inventory', parent: '/dashboard/D&AIXOps' },
		'/D&AIXOps/misc': { label: 'Miscellaneous Dashboards', parent: '/dashboard/D&AIXOps' },
		// '/D&AIXOps/Summary':{label: 'D&AI Monthly XOps Dashboards', parent: '/D&AIXOps/Summary'},
		// '/D&AIXOps/Summary':{label: 'D&AI Monthly XOps Dashboards', parent: '/D&AIXOps/Summary'},
	};

	const truncate = (label: string, max = 4) => (label.length > max ? `${label.slice(0, max)}…` : label);

	type Crumb = { label: string; path: string };

	const findBestKey = (pathname: string) => {
		if (breadcrumbIndex[pathname]) return pathname;
		// fallback: longest prefix key
		let best: string | undefined;
		for (const key of Object.keys(breadcrumbIndex)) {
			if (pathname.startsWith(key + '/') || pathname === key) {
				if (!best || key.length > best.length) best = key;
			}
		}
		return best;
	};

	const getXopsDashboardName = (
		pathname: string
	): string | null => {
		const xopsBasePath =
			'/dashboard/xops-intelligence/';

		if (!pathname.startsWith(xopsBasePath)) {
			return null;
		}

		const slug = pathname
			.replace(xopsBasePath, '')
			.split('/')[0];

		if (!slug) {
			return null;
		}

		const abbreviationMap: Record<string, string> = {
			l3: 'L3',
			ibp: 'IBP',
			sr: 'SR',
			biops: 'BIOps',
			sla: 'SLA'
		};

		return slug
			.split('-')
			.map((word) => {
				const normalizedWord = word.toLowerCase();

				if (abbreviationMap[normalizedWord]) {
					return abbreviationMap[normalizedWord];
				}

				return (
					word.charAt(0).toUpperCase() +
					word.slice(1)
				);
			})
			.join(' ');
	};

	const getBreadcrumbs = (
		pathname: string
	): Crumb[] => {
		const xopsBasePath =
			'/dashboard/xops-intelligence';

		const isXopsDashboardRoute =
			pathname.startsWith(`${xopsBasePath}/`);

		if (isXopsDashboardRoute) {
			const dashboardName =
				getXopsDashboardName(pathname);

			return [
				{
					label: 'XOps Intelligence',
					path: xopsBasePath,
				},
				{
					label: dashboardName ?? '',
					path: pathname,
				},
			];
		}

		 if (
			pathname === '/data-pipeline/executiveOps/pepsellPepIrisRecon' &&
			fromDashboardType &&
			fromProgramName
		) {
			const parentPath = `/data-pipeline/executiveOps/${fromDashboardType}`;
			const parentChain = getBreadcrumbs(parentPath);
			return [
				...parentChain,
				{ label: fromProgramName, path: pathname },
			];
		}

		const startKey = findBestKey(pathname);

		if (!startKey) {
			return [];
		}

		const chain: Crumb[] = [];
		let k: string | undefined = startKey;

		while (k) {
			const node: any =
				breadcrumbIndex[k];

			chain.push({
				label: node.label,
				path: k,
			});

			k = node.parent;
		}

		return chain.reverse(); // root → ... → current
	};

	const crumbs = getBreadcrumbs(location.pathname);

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const getInitials = (name: string) => {
		const names = name.split(' ');
		return names.map((n) => n[0].toUpperCase()).join('');
	};

	const logout = async () => {
		sessionStorage.removeItem('pepwise_chat_session');
		sessionStorage.removeItem('pepwise_chat_messages');
		sessionStorage.removeItem('pepwise_chat_isExpanded');
		if (ssoLogin) {
			try {
				window.location.replace('/sso/logout');
			} catch (error) {
				console.error('Logout error:', error);
			}
		} else {
			deleteCookie(sessionCookie);
			setAuthToken(dispatch, null);
			setAuthenticated(dispatch, false);
		}
	};

	// navigate to landing page
	const handleHomeClick = () => {
		// Tableau drilldown filter changes
		localStorage.removeItem('selectedDateTime');
		localStorage.removeItem('selectedSector');
		localStorage.removeItem('prestoSelectedDateTime');
		localStorage.removeItem('boxRows');
		localStorage.removeItem('added-schedules_user_col_pref');
		localStorage.removeItem('project-list_user_col_pref');
		window.location.href = '/LandingPage';
	};


	// [context Api]
	const [, dispatchCache] = useCacheDataController();


	// [Detect route change]
	const [, setTeradataDailyLastSyncDate] = React.useState<string | null>('');

	const getLastSyncLabel = (
		todaysDate: moment.Moment,
		lastDays: moment.Moment
	): string => {
		const diffDays = todaysDate.diff(lastDays, 'days');

		if (diffDays === 0) {
			return 'Today';
		}

		return diffDays > 1
			? `${diffDays} days ago`
			: `${diffDays} day ago`;
	};

	const clearIfExpired = (
		fetchKey: string,
		valueKey: string
	): void => {
		const stored = localStorage.getItem(fetchKey);
		if (!stored) return;

		const diffMinutes =
			(Date.now() - new Date(stored).getTime()) / 60000;

		if (diffMinutes / 60 >= 24) {
			localStorage.removeItem(valueKey);
		}
	};

	const fetchAndStoreLastSync = async (
		fetchFn: Promise<any>,
		storageKey: string,
		fetchStorageKey: string,
		setter: (value: string) => void,
		todaysDate: moment.Moment
	) => {
		const res = await fetchFn;
		const lastSyncDate = res?.data?.result[0]?.lastSyncTime;

		const lastDays = moment([
			new Date(lastSyncDate).getFullYear(),
			new Date(lastSyncDate).getMonth() + 1,
			new Date(lastSyncDate).getDate()
		]);

		const label = getLastSyncLabel(todaysDate, lastDays);

		localStorage.setItem(storageKey, label);
		localStorage.setItem(fetchStorageKey, new Date().toISOString());

		setter(label);
	};

	React.useEffect(() => {
		let todaysDate = moment([new Date().getFullYear(), new Date().getMonth() + 1, new Date().getDate()]);
		const isDataEstate = window.location.pathname.includes('/dataestate/');

		// --- Expiry cleanup ---
		clearIfExpired('fetchAzureCoSumLastSyncDate', 'azureCoSumLastSyncDate');
		clearIfExpired('fetchdeMetricsLastSyncDate', 'deMetricsLastSyncDate');
		clearIfExpired('fetchteradataDailyLastSyncDate', 'teradataDailyLastSyncDate');
		clearIfExpired('fetchteradataStorageLastSyncDate', 'teradataStorageLastSyncDate');

		if (!isDataEstate) return;

		// --- Azure ---
		if (!localStorage.getItem('azureCoSumLastSyncDate')) {
			FetchFinOpsOverviewCloudCost('lastSync')
				.then((res) =>
					fetchAndStoreLastSync(
						Promise.resolve(res),
						'azureCoSumLastSyncDate',
						'fetchAzureCoSumLastSyncDate',
						(v) => setAzureCostSummaryLastSyncDate(dispatchCache, v),
						todaysDate
					)
				)
				.catch(console.error);
		} else {
			setAzureCostSummaryLastSyncDate(
				dispatchCache,
				localStorage.getItem('azureCoSumLastSyncDate')
			);
		}

		// --- deMetrics ---
		if (!localStorage.getItem('deMetricsLastSyncDate')) {
			fetchDataEstateLastSyncData('de-metrics')
				.then((res) =>
					fetchAndStoreLastSync(
						Promise.resolve(res),
						'deMetricsLastSyncDate',
						'fetchdeMetricsLastSyncDate',
						(v) => setDemetricsLastSyncDate(dispatchCache, v),
						todaysDate
					)
				)
				.catch(console.error);
		} else {
			setDemetricsLastSyncDate(
				dispatchCache,
				localStorage.getItem('deMetricsLastSyncDate')
			);
		}

		// --- Teradata Daily ---
		if (!localStorage.getItem('teradataDailyLastSyncDate')) {
			fetchDataEstateLastSyncData('teradata-daily')
				.then((res) =>
					fetchAndStoreLastSync(
						Promise.resolve(res),
						'teradataDailyLastSyncDate',
						'fetchteradataDailyLastSyncDate',
						setTeradataDailyLastSyncDate,
						todaysDate
					)
				)
				.catch(console.error);
		} else {
			setTeradataDailyLastSyncDate(
				localStorage.getItem('teradataDailyLastSyncDate')
			);
		}

		// --- Teradata Storage ---
		if (!localStorage.getItem('teradataStorageLastSyncDate')) {
			fetchDataEstateLastSyncData('teradata-storage')
				.then((res) =>
					fetchAndStoreLastSync(
						Promise.resolve(res),
						'teradataStorageLastSyncDate',
						'fetchteradataStorageLastSyncDate',
						(v) => setTeradataStorageLastSyncDate(dispatchCache, v),
						todaysDate
					)
				)
				.catch(console.error);
		} else {
			setTeradataStorageLastSyncDate(
				dispatchCache,
				localStorage.getItem('teradataStorageLastSyncDate')
			);
		}
	}, [location.pathname]);

	const isSmall = useMediaQuery(theme.breakpoints.down("sm")); // < 800
	const lastCrumb = crumbs[crumbs.length - 1];

	if (location.pathname === '/' || location.pathname === '/userLogin' || location.pathname === '/signup') {
		return null;
	}

	return (
		<Box
			sx={{
				position: 'fixed',
				top: 0,
				width: '100%',
				zIndex: 100
			}}
		>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'row',
					alignItems: 'center',
					backgroundColor: 'Header.background',
					height: '40px',
					ml: '60px'
				}}
			>
				<Toolbar sx={{ minHeight: '40px !important', px: 2, width: '100%' }}>
					<Box sx={{ display: 'flex', alignItems: 'center', overflowX: 'auto', minWidth: { xs: '100px', md: '250px' } }}>
						<HomeIcon
							onClick={!isLandingPage ? handleHomeClick : undefined}
							style={{
								cursor: !isLandingPage ? 'pointer' : 'default',
							}}
						/>
						{isSmall ? (
							<>
								{crumbs.length > 1 && (
									<>
										<Typography sx={{ mx: 1, color: 'Header.text' }}>/</Typography>
										<Typography variant="subHeader" sx={{ color: 'Header.text' }}>
											…
										</Typography>
									</>
								)}

								{lastCrumb && (
									<>
										<Typography sx={{ mx: 1, color: 'Header.text' }}>/</Typography>
										<Typography
											aria-current="page"
											variant="subHeader"
											sx={{
												color: 'Header.text',
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												maxWidth: '8ch'
											}}
											title={lastCrumb.label}
										>
											{lastCrumb.label}
										</Typography>
									</>
								)}
							</>
						) : (
							crumbs.filter(c => c.label).map((c, i, filteredCrumbs) => {
								const isLast = i === filteredCrumbs.length - 1;
								const shouldTruncate = filteredCrumbs.length > 2 && !isLast; // truncate only parents if > 2 crumbs
								const displayLabel = shouldTruncate ? truncate(c.label, 6) : c.label;
								const isDataPipelineObservability = c.label === 'Data Pipeline Observability';
								const isDataKPIAndAlerts = c.label === 'Data KPI and Alerts';
								const isFinanceFinOpsDashboard = c.label === 'Finance(FinOps) Dashboard';
								const isPlatformObservability = c.label === 'Platform Observability';
								const isDAIExecutiveDashboard = c.label === 'D&AI Executive Dashboard';
								const isDAIMonthlyXOpsDashboard = c.label === 'D&AI Monthly XOps Dashboards';

								return (
									<Box key={c.path} sx={{ display: 'flex', alignItems: 'center' }}>
										<Typography sx={{ mx: 1, color: 'Header.text' }}>/</Typography>

										{isLast ? (
											// Last item: full label, not clickable
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
												<Typography aria-current="page" variant="subHeader" sx={{ color: 'Header.text' }}>
													{displayLabel}
												</Typography>
												{isDataPipelineObservability && (
													<Tooltip
														title={
															<Box sx={{ p: 1 }}>
																<Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: 'black !important' }}>
																	About Dashboard
																</Typography>
																<Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: 'black !important' }}>
																	Monitors pipeline health, failures, data freshness and processing efficiency so users can quickly spot anomalies and ensure end‑to‑end data reliability.
																</Typography>
															</Box>
														}
														arrow
														placement="bottom"
														slotProps={{
															popper: {
																modifiers: [
																	{
																		name: "offset",
																		options: {
																			offset: [0, 16],
																		},
																	},
																],
															},
															tooltip: {
																sx: {
																	bgcolor: 'white !important',
																	color: 'black !important',
																	boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
																	maxWidth: 400,
																	fontSize: '0.875rem',
																	mt: '15px !important',
																	'& .MuiTooltip-arrow': {
																		color: 'white !important'
																	}
																}
															}
														}}
													>
														<InfoIcon
															sx={{
																fontSize: '1rem',
																color: '#A9ADCB !important',
																cursor: 'pointer',
																'&:hover': {
																	opacity: 0.8
																}
															}}
														/>
													</Tooltip>
												)}
												{isDAIMonthlyXOpsDashboard && (
													<Tooltip
														title={
															<Box sx={{ p: 1 }}>
																<Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: 'black !important' }}>
																	About Dashboard
																</Typography>
																<Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: 'black !important' }}>
																	Shows monthly operational KPIs across DataOps, FinOps, AI/ML Ops, etc. Surfacing process efficiency, pipeline stability and delivery throughput to monitor operational trends and guide optimization actions.
																</Typography>
															</Box>
														}
														arrow
														placement="bottom"
														slotProps={{
															popper: {
																modifiers: [
																	{
																		name: "offset",
																		options: {
																			offset: [0, 16],
																		},
																	},
																],
															},
															tooltip: {
																sx: {
																	bgcolor: 'white !important',
																	color: 'black !important',
																	boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
																	maxWidth: 400,
																	fontSize: '0.875rem',
																	mt: '15px !important',
																	'& .MuiTooltip-arrow': {
																		color: 'white !important'
																	}
																}
															}
														}}
													>
														<InfoIcon
															sx={{
																fontSize: '1rem',
																color: '#A9ADCB !important',
																cursor: 'pointer',
																'&:hover': {
																	opacity: 0.8
																}
															}}
														/>
													</Tooltip>
												)}
												{isDataKPIAndAlerts && (
													<Tooltip
														sx={{ marginTop: '20px', backgroundColor: 'red' }}
														title={
															<Box sx={{ p: 1 }}>
																<Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: 'black !important' }}>
																	About Dashboard
																</Typography>
																<Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: 'black !important' }}>
																	Centralizes key data quality, latency, freshness and SLA metrics with alerting to help teams rapidly respond to data issues.
																</Typography>
															</Box>
														}
														arrow
														placement="bottom"
														slotProps={{
															popper: {
																modifiers: [
																	{
																		name: "offset",
																		options: {
																			offset: [0, 16],
																		},
																	},
																],
															},
															tooltip: {
																sx: {
																	bgcolor: 'white !important',
																	color: 'black !important',
																	boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
																	maxWidth: 400,
																	fontSize: '0.875rem',
																	mt: '35px !important',
																	'& .MuiTooltip-arrow': {
																		color: 'white !important'
																	}
																}
															}
														}}
													>
														<InfoIcon
															sx={{
																fontSize: '1rem',
																color: '#A9ADCB !important',
																cursor: 'pointer',
																'&:hover': {
																	opacity: 0.8
																}
															}}
														/>
													</Tooltip>
												)}
												{isFinanceFinOpsDashboard && (
													<Tooltip
														title={
															<Box sx={{ p: 1 }}>
																<Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: 'black !important' }}>
																	About Dashboard
																</Typography>
																<Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: 'black !important' }}>
																	Visualize resource allocation, skill distribution and costing metrics to help optimize staffing, capacity planning and financial alignment.
																</Typography>
															</Box>
														}
														arrow
														placement="bottom"
														slotProps={{
															popper: {
																modifiers: [
																	{
																		name: "offset",
																		options: {
																			offset: [0, 16],
																		},
																	},
																],
															},
															tooltip: {
																sx: {
																	bgcolor: 'white !important',
																	color: 'black !important',
																	boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
																	maxWidth: 400,
																	fontSize: '0.875rem',
																	mt: '15px !important',
																	'& .MuiTooltip-arrow': {
																		color: 'white !important'
																	}
																}
															}
														}}
													>
														<InfoIcon
															sx={{
																fontSize: '1rem',
																color: '#A9ADCB !important',
																cursor: 'pointer',
																'&:hover': {
																	opacity: 0.8
																}
															}}
														/>
													</Tooltip>
												)}
												{isPlatformObservability && (
													<Tooltip
														title={
															<Box sx={{ p: 1 }}>
																<Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: 'black !important' }}>
																	About Dashboard
																</Typography>
																<Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: 'black !important' }}>
																	Tracks platform performance, usage, capacity and availability of metrics to ensure stability and proactively address infrastructure needs
																</Typography>
															</Box>
														}
														arrow
														placement="bottom"
														slotProps={{
															popper: {
																modifiers: [
																	{
																		name: "offset",
																		options: {
																			offset: [0, 16],
																		},
																	},
																],
															},
															tooltip: {
																sx: {
																	bgcolor: 'white !important',
																	color: 'black !important',
																	boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
																	maxWidth: 400,
																	fontSize: '0.875rem',
																	mt: '15px !important',
																	'& .MuiTooltip-arrow': {
																		color: 'white !important'
																	}
																}
															}
														}}
													>
														<InfoIcon
															sx={{
																fontSize: '1rem',
																color: '#A9ADCB !important',
																cursor: 'pointer',
																'&:hover': {
																	opacity: 0.8
																}
															}}
														/>
													</Tooltip>
												)}
												{isDAIExecutiveDashboard && (
													<Tooltip
														title={
															<Box sx={{ p: 1 }}>
																<Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: 'black !important' }}>
																	About Dashboard
																</Typography>
																<Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: 'black !important' }}>
																	Provides an executive overview of D&AI performance, highlighting KPIs that track delivery of health, productivity, cost and value realization—helping leaders identify trends and act on risks or opportunities
																</Typography>
															</Box>
														}
														arrow
														placement="bottom"
														slotProps={{
															popper: {
																modifiers: [
																	{
																		name: "offset",
																		options: {
																			offset: [0, 16],
																		},
																	},
																],
															},
															tooltip: {
																sx: {
																	bgcolor: 'white !important',
																	color: 'black !important',
																	boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
																	maxWidth: 400,
																	fontSize: '0.875rem',
																	mt: '15px !important',
																	'& .MuiTooltip-arrow': {
																		color: 'white !important'
																	}
																}
															}
														}}
													>
														<InfoIcon
															sx={{
																fontSize: '1rem',
																color: '#A9ADCB !important',
																cursor: 'pointer',
																'&:hover': {
																	opacity: 0.8
																}
															}}
														/>
													</Tooltip>
												)}
											</Box>
										) : (
											// Parent items: clickable, maybe truncated
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
												<Typography
													component={Link}
													to={c.path}
													variant="subHeader"
													sx={{
														color: 'Header.text',
														textDecoration: 'none',
														'&:hover': { textDecoration: 'underline' }
													}}
												>
													{displayLabel}
												</Typography>
												{isDataPipelineObservability && (
													<Tooltip
														title={
															<Box sx={{ p: 1 }}>
																<Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: 'black !important' }}>
																	About Dashboard
																</Typography>
																<Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: 'black !important' }}>
																	Monitors pipeline health, failures, data freshness and processing efficiency so users can quickly spot anomalies and ensure end‑to‑end data reliability.
																</Typography>
															</Box>
														}
														arrow
														placement="bottom"
														slotProps={{
															tooltip: {
																sx: {
																	bgcolor: 'white !important',
																	color: 'black !important',
																	boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
																	maxWidth: 400,
																	fontSize: '0.875rem',
																	mt: '15px !important',
																	'& .MuiTooltip-arrow': {
																		color: 'white !important'
																	}
																}
															}
														}}
													>
														<InfoIcon
															sx={{
																fontSize: '1rem',
																color: '#A9ADCB !important',
																cursor: 'pointer',
																'&:hover': {
																	opacity: 0.8
																}
															}}
														/>
													</Tooltip>
												)}
												{isDAIMonthlyXOpsDashboard && (
													<Tooltip
														title={
															<Box sx={{ p: 1 }}>
																<Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: 'black !important' }}>
																	About Dashboard
																</Typography>
																<Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: 'black !important' }}>
																	Shows monthly operational KPIs across DataOps, FinOps, AI/ML Ops, etc. Surfacing process efficiency, pipeline stability and delivery throughput to monitor operational trends and guide optimization actions.
																</Typography>
															</Box>
														}
														arrow
														placement="bottom"
														slotProps={{
															tooltip: {
																sx: {
																	bgcolor: 'white !important',
																	color: 'black !important',
																	boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
																	maxWidth: 400,
																	fontSize: '0.875rem',
																	mt: '15px !important',
																	'& .MuiTooltip-arrow': {
																		color: 'white !important'
																	}
																}
															}
														}}
													>
														<InfoIcon
															sx={{
																fontSize: '1rem',
																color: '#A9ADCB !important',
																cursor: 'pointer',
																'&:hover': {
																	opacity: 0.8
																}
															}}
														/>
													</Tooltip>
												)}
												{isDataKPIAndAlerts && (
													<Tooltip
														title={
															<Box sx={{ p: 1 }}>
																<Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: 'black !important' }}>
																	About Dashboard
																</Typography>
																<Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: 'black !important' }}>
																	Centralizes key data quality, latency, freshness and SLA metrics with alerting to help teams rapidly respond to data issues.
																</Typography>
															</Box>
														}
														arrow
														placement="bottom"
														slotProps={{
															tooltip: {
																sx: {
																	bgcolor: 'white !important',
																	color: 'black !important',
																	boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
																	maxWidth: 400,
																	fontSize: '0.875rem',
																	mt: '15px !important',
																	'& .MuiTooltip-arrow': {
																		color: 'white !important'
																	}
																}
															}
														}}
													>
														<InfoIcon
															sx={{
																fontSize: '1rem',
																color: '#A9ADCB !important',
																cursor: 'pointer',
																'&:hover': {
																	opacity: 0.8
																}
															}}
														/>
													</Tooltip>
												)}
												{isFinanceFinOpsDashboard && (
													<Tooltip
														title={
															<Box sx={{ p: 1 }}>
																<Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: 'black !important' }}>
																	About Dashboard
																</Typography>
																<Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: 'black !important' }}>
																	Visualize resource allocation, skill distribution and costing metrics to help optimize staffing, capacity planning and financial alignment.
																</Typography>
															</Box>
														}
														arrow
														placement="bottom"
														slotProps={{
															tooltip: {
																sx: {
																	bgcolor: 'white !important',
																	color: 'black !important',
																	boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
																	maxWidth: 400,
																	fontSize: '0.875rem',
																	mt: '15px !important',
																	'& .MuiTooltip-arrow': {
																		color: 'white !important'
																	}
																}
															}
														}}
													>
														<InfoIcon
															sx={{
																fontSize: '1rem',
																color: '#A9ADCB !important',
																cursor: 'pointer',
																'&:hover': {
																	opacity: 0.8
																}
															}}
														/>
													</Tooltip>
												)}
												{isPlatformObservability && (
													<Tooltip
														title={
															<Box sx={{ p: 1 }}>
																<Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: 'black !important' }}>
																	About Dashboard
																</Typography>
																<Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: 'black !important' }}>
																	Tracks platform performance, usage, capacity and availability of metrics to ensure stability and proactively address infrastructure needs
																</Typography>
															</Box>
														}
														arrow
														placement="bottom"
														slotProps={{
															tooltip: {
																sx: {
																	bgcolor: 'white !important',
																	color: 'black !important',
																	boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
																	maxWidth: 400,
																	fontSize: '0.875rem',
																	mt: '15px !important',
																	'& .MuiTooltip-arrow': {
																		color: 'white !important'
																	}
																}
															}
														}}
													>
														<InfoIcon
															sx={{
																fontSize: '1rem',
																color: '#A9ADCB !important',
																cursor: 'pointer',
																'&:hover': {
																	opacity: 0.8
																}
															}}
														/>
													</Tooltip>
												)}
												{isDAIExecutiveDashboard && (
													<Tooltip
														title={
															<Box sx={{ p: 1 }}>
																<Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1, color: 'black !important' }}>
																	About Dashboard
																</Typography>
																<Typography variant="body2" sx={{ whiteSpace: 'pre-line', color: 'black !important' }}>
																	Provides an executive overview of D&AI performance, highlighting KPIs that track delivery of health, productivity, cost and value realization—helping leaders identify trends and act on risks or opportunities
																</Typography>
															</Box>
														}
														arrow
														placement="bottom"
														slotProps={{
															tooltip: {
																sx: {
																	bgcolor: 'white !important',
																	color: 'black !important',
																	boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
																	maxWidth: 400,
																	fontSize: '0.875rem',
																	mt: '15px !important',
																	'& .MuiTooltip-arrow': {
																		color: 'white !important'
																	}
																}
															}
														}}
													>
														<InfoIcon
															sx={{
																fontSize: '1rem',
																color: '#A9ADCB !important',
																cursor: 'pointer',
																'&:hover': {
																	opacity: 0.8
																}
															}}
														/>
													</Tooltip>
												)}
											</Box>
										)}
									</Box>
								);
							})
						)}
					</Box>


					<Box sx={{ flexGrow: 1 }} />

					<Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1, md: 3 } }}>

						<GlobalSearch />

						<Typography variant="bodyText" sx={{ color: 'Header.textSecondary', display: { xs: 'none', sm: 'none', md: 'inline' } }}>
							{moment().tz(Intl.DateTimeFormat().resolvedOptions().timeZone).format('dddd, MMM DD, YYYY, hh:mm A z')}
						</Typography>

						{/* <Box sx={{ paddingTop: '11px' }}>
							{showLandingPageNotification?.()}
						</Box> */}

						{/* <Tooltip title="Settings">
							<IconButton disabled>
								<SettingsIcon fontSize="small" />
							</IconButton>
						</Tooltip> */}

						<Tooltip title="">
							<IconButton id="header-user-icon" onClick={handleClick}>
								<UserIcon fontSize="small" />
							</IconButton>
						</Tooltip>

						{isLandingPage && (
							<Tooltip title="Start Help Tour">
								<IconButton id="header-help-icon" onClick={handleHelpClick}>
									<HelpIcon />
								</IconButton>
							</Tooltip>
						)}

						<Box>
							{showFullScreen?.()}
						</Box>

						<Menu
							anchorEl={anchorEl}
							open={open}
							onClose={handleClose}
							MenuListProps={{
								'aria-labelledby': 'basic-button',
								disablePadding: true
							}}
						>
							<Box py={2} sx={{ backgroundColor: 'Header.userMenuBg' }}>
								<Box display="flex" alignItems="center" gap={2} px={2}>
									<Avatar
										sx={{
											bgcolor: 'Header.UserMenuAvatarBg',
											width: 48,
											height: 48,
											fontSize: 14,
											fontWeight: 600,
											color: 'Header.UserMenuAvatarText'
										}}
									>
										{getInitials(getDecodedToken()?.firstName)}
									</Avatar>

									<Box>
										<Typography
											variant="bodyText"
											display="block"
											lineHeight="25px"
											sx={{ color: 'Header.text' }}
										>
											{getDecodedToken()?.firstName} {getDecodedToken()?.lastName}
										</Typography>
										<Typography variant="caption" display="block" sx={{ color: 'Header.text', fontStyle: 'italic' }}>
											{getDecodedToken()?.email}
										</Typography>
									</Box>
								</Box>

								<Divider sx={{ my: 2, borderColor: 'text.subtle' }} />

								<Box display="flex" alignItems="center" gap={2} px={4}>
									<LogoutIcon fontSize="small" />
									<Button onClick={logout} sx={{ color: 'Header.text' }}>
										Logout
									</Button>
								</Box>
							</Box>
						</Menu>
					</Box>
				</Toolbar>
			</Box>

			{![
				'/',
				'/LandingPage',
				'/dashboard/dataOpsLanding',
				'/data-pipeline/home/executiveOps',
				'/dashboard/financeOpsLanding',
				'/dashboard/D&AIOpsLanding',
				'/dashboard/programExecutiveLanding',
				'/dashboard/platformCostLanding',
				'/dashboard/alertKpiLanding',
				'/dashboard/sessionTimeOut',
				'/dashboard/monthlyXOpsLanding',
				'/dashboard/xops-intelligence',
				'/pepwisev2/feedback'
			].includes(location.pathname) && (
					<Box sx={{ height: '36px', backgroundColor: 'Header.TabPagerHeaderBg', borderBottom: `1px solid ${colors.Header.TabPageHeaderBorder}` }}>
						<TabPageHeader />
					</Box>)}

			<DynamicBannerOverlay
				viewer={bannerViewer}
				placement="below-tabs"
				sidebarOffset={60}
			/>

			{location.pathname !== '/dataGrowth/dashboard' &&
				location.pathname !== '/databricks/summary/dashboard' &&
				location.pathname !== '/databricks/resource/dashboard' &&
				location.pathname !== '/databricks/jobs/dashboard' &&
				location.pathname !== '/databricks/costSummary/dashboard' &&
				location.pathname !== '/databricks/governance/dashboard' &&
				location.pathname !== '/databricks/infrastructure/dashboard' &&
				// location.pathname !== '/powerBI/capacity/dashboard' &&
				// location.pathname !== '/powerBI/capacityObservability/dashboard' &&
				// location.pathname !== '/powerBI/gatewayObservability/dashboard' &&
				// location.pathname !== '/powerBI/adoptionMetrics/dashboard' &&
				// location.pathname !== '/powerBI/dataset/dashboard' &&
				// location.pathname !== '/powerBI/usage/dashboard' &&
				// location.pathname !== '/powerBI/datagrowth/dashboard' &&
				// location.pathname !== '/powerBI/infrastructure/dashboard' &&
				location.pathname !== '/powerBI/userActivity/dashboard' &&
				location.pathname !== '/dataManagementFramework/healthSummary/dashboard' &&
				location.pathname !== '/dataManagementFramework/Catalogues/dashboard' &&
				location.pathname !== '/verificationValidation/dashboard' &&
				location.pathname !== '/verificationValidation/powerbi/dashboard' &&
				!location.pathname.includes('/tableau') &&
				!location.pathname.includes('/machineLearning') &&
				!location.pathname.includes('/platformCostObs') &&
				!location.pathname.includes('/presto') &&
				!location.pathname.includes('/controlsCompliance') &&
				!location.pathname.includes('/ibp') &&
				!location.pathname.includes('/dataGrowth') &&
				!location.pathname.includes('/dataestate') &&
				!location.pathname.includes('/databricks') &&
				!location.pathname.includes('/finops') &&
				!location.pathname.includes('/data-pipeline') && (
					<Box>
						<NotificationBar notificationData={notificationData} />
					</Box>
				)}
		</Box>
	);
};

export default Header;
